import SECTION_SCHEMA, {
  getFormStepKeys,
  isExperimentalContentStep,
  resolveStepFields,
  SECTION_KEYS
} from "../schema/sections"
import {
  hasVerifiedProcessorDescriptor,
  isStepComplete,
  validateQuestion
} from "./sectionValidation"
import { evalShowIf, visibleQuestions } from "./showIf"

const getDeep = (obj, path) => {
  if (!path || !obj) return undefined
  return path.split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), obj)
}

// Empty values (undefined / '' / [] / null) never count. Numeric 0 is
// ambiguous. sometimes a real answer (refund_window_days = 0 = no refunds)
// and sometimes a sentinel for unset. Each schema question opts in via
// `acceptZero: true` when 0 is meaningful for that question.
const isAnswered = (value, q) => {
  const type = q?.type
  if (value === null || value === undefined) return false
  if (type === "chip-multi") return Array.isArray(value) && value.length > 0
  // Multi-value free text (e.g. billing descriptors): a non-empty list, or a
  // legacy single string, counts as answered.
  if (type === "text-multi") {
    if (Array.isArray(value)) return value.length > 0
    return typeof value === "string" && value.trim() !== ""
  }
  if (type === "upload") return !!(value && (value.s3_key || value.url))
  if (type === "number") {
    if (value === "") return false
    const n = Number(value)
    if (Number.isNaN(n)) return false
    if (n === 0) return !!q?.acceptZero
    return true
  }
  if (type === "textarea") {
    // Mirror the validator (sectionValidation.js): a REQUIRED textarea is only
    // "answered" once it meets the min length (default 20), so the progress ring
    // can't read complete while Next is still blocked on the same field. An
    // optional textarea counts as answered on any non-empty value.
    if (typeof value !== "string" || value.trim() === "") return false
    return q?.required ? value.trim().length >= (q?.min ?? 20) : true
  }
  if (typeof value === "string") {
    return value.trim() !== ""
  }
  return true
}

// hasVerifiedProcessorDescriptor now lives in sectionValidation.js, so the validator
// and this counter share one definition and cannot drift apart again.

// Treats a chip-multi as answered if EITHER the main key has selections OR the
// user added any custom chips at the `<key>_custom` companion path. Without
// this, a question answered only with "+ Other" custom chips would silently
// count as unanswered in the progress badge.
const isQuestionAnswered = (sectionState, q, buildingBlocks) => {
  const main = getDeep(sectionState, q.key)
  if (q.type === "chip-multi" && q.allowOther) {
    const customs = getDeep(sectionState, `${q.key}_custom`)
    if (Array.isArray(customs) && customs.length > 0) return true
  }
  // A processor-verified descriptor field counts as answered off the locked verified
  // value even when the editable list is empty, or the section could never reach 100%.
  if (
    q.type === "text-multi" &&
    q.verifiedFrom === "processors" &&
    hasVerifiedProcessorDescriptor(buildingBlocks)
  )
    return true
  if (!isAnswered(main, q)) return false
  // A non-empty but INVALID value (e.g. a malformed optional URL or email) blocks
  // Next via validateQuestion, so it must not read as answered either, or the
  // progress ring shows complete while Next stays disabled on the same field.
  return validateQuestion(q, main) == null
}

// Returns { answered, total } for one section so the UI can render the
// Justt-style "X/Y" badge in the left rail.
export const sectionAnswerCount = (sectionKey, buildingBlocks) => {
  const schema = visibleQuestions(SECTION_SCHEMA[sectionKey] || [], buildingBlocks)
  const sectionState = buildingBlocks?.[sectionKey] ?? {}
  // The Integrate step has no form questions; it is "done" when a payment processor is
  // connected (the dispute sync + verified descriptor depend on it), so it counts as 1/1.
  // This processor gate is intentionally redundant with required_information's gate
  // (required_information owns the processor requirement); integrations is never
  // required for activation, and Footer.handleFinish excludes it from the
  // finish-completeness count.
  if (sectionKey === "integrations") {
    const hasProcessor = (buildingBlocks?.processors?.connected || []).length > 0
    return { answered: hasProcessor ? 1 : 0, total: 1 }
  }
  // Enrichment is an always-optional connect step: connecting support / shipping /
  // marketing tools strengthens evidence but is never required, so it reads complete
  // (1/1) regardless of how many tools are connected. Keeps it from ever blocking the
  // finished-profile state or the per-processor "N of M" completion.
  if (sectionKey === "enrichment") {
    return { answered: 1, total: 1 }
  }
  const answered = schema.filter((q) => isQuestionAnswered(sectionState, q, buildingBlocks)).length
  return { answered, total: schema.length }
}

// EXPERIMENTAL: X/Y count for a step that aggregates fields across buckets. Mirrors
// sectionAnswerCount but resolves each field's def + state from its own section. Only
// visible (showIf-passing) fields count, so the rail count matches what's on screen.
export const stepAnswerCount = (stepKey, buildingBlocks) => {
  const fields = resolveStepFields(stepKey).filter((f) => evalShowIf(f.def.showIf, buildingBlocks))
  let answered = 0
  for (const f of fields) {
    const sectionState = buildingBlocks?.[f.section] ?? {}
    if (isQuestionAnswered(sectionState, f.def, buildingBlocks)) answered += 1
  }
  return { answered, total: fields.length }
}

// Unified per-step count for the rail: an experimental content step aggregates its
// buckets; every other step (Integrate, Enrichment, and all non-experimental sections)
// keeps the existing per-section count.
export const stepCount = (stepKey, buildingBlocks) =>
  isExperimentalContentStep(stepKey)
    ? stepAnswerCount(stepKey, buildingBlocks)
    : sectionAnswerCount(stepKey, buildingBlocks)

export const sectionProgress = (sectionKey, buildingBlocks) => {
  const { answered, total } = sectionAnswerCount(sectionKey, buildingBlocks)
  if (total === 0) return 0
  return Math.round((answered / total) * 100)
}

export const allSectionProgress = (buildingBlocks) => {
  const out = {}
  for (const key of SECTION_KEYS) {
    out[key] = sectionProgress(key, buildingBlocks)
  }
  return out
}

export const allSectionCounts = (buildingBlocks) => {
  const out = {}
  for (const key of SECTION_KEYS) {
    out[key] = sectionAnswerCount(key, buildingBlocks)
  }
  return out
}

// Per-connected-processor completion, for the processor switcher shown at the top of
// steps 2-6 and on the summary. Counts the ACTIVE flow's form steps with the SAME
// required-only predicate the rail uses (isStepComplete over getFormStepKeys), so the
// switcher's "Done" / "{{done}} of {{total}}" agrees with the rail's green checks. The
// old classic-SECTION_KEYS count included fields the experimental flow never renders,
// so an account-based processor could never reach "Done"; this fixes that.
// Steps that are always passable because everything in them is optional (Enrichment is
// "connect what you have", the Evidence locker is "upload what you have"). They must not
// read "Done" before the merchant has put anything in: on a brand-new profile that made
// the rail claim three finished steps while the merchant had touched nothing. They read
// "Optional" until they hold something, and "Done" once they do.
const OPTIONAL_STEP_KEYS = ["enrichment", "evidence_locker", "evidence_vault"]
export const isOptionalStep = (stepKey) => OPTIONAL_STEP_KEYS.includes(stepKey)
export const optionalStepStarted = (stepKey, buildingBlocks) => {
  if (stepKey === "enrichment") {
    const connected = buildingBlocks?.enrichment?.connected
    return Array.isArray(connected) && connected.length > 0
  }
  return stepCount(stepKey, buildingBlocks).answered > 0
}

export const processorStatuses = (fullBuildingBlocks, connectedProcessors) => {
  const bb = fullBuildingBlocks || {}
  const stepKeys = getFormStepKeys()
  return (connectedProcessors || []).map((slug) => {
    const view = { ...(bb.byProcessor?.[slug] || {}), processors: bb.processors, tools: bb.tools }
    // `done` counts what the rail shows as Done, so the switcher's "N of M" and the
    // rail's badges tell the same story: an optional step nobody has touched reads
    // "Optional" in the rail and is not counted here either.
    const done = stepKeys.filter(
      (k) => isStepComplete(k, view) && !(isOptionalStep(k) && !optionalStepStarted(k, view))
    ).length
    // The Done badge still follows the real gate (every step passable), which optional
    // steps satisfy by definition, so a merchant who filled every required field is not
    // held back by an untouched optional one.
    const complete = stepKeys.every((k) => isStepComplete(k, view))
    return { slug, done, total: stepKeys.length, complete }
  })
}

// True when every section has every visible field answered (answered === total),
// optional ones included. NOTE this is NOT the bar the green step checks or the
// finished-profile collapse use any more: both moved to allStepsComplete
// (sectionValidation.js), the required-fields gate that Next and Finish enforce, so a
// step could not read "1/4" while the wizard let you walk past it. Kept for a caller
// that genuinely wants "everything filled in, optional included".
export const allSectionsComplete = (buildingBlocks) =>
  SECTION_KEYS.every((key) => {
    const c = sectionAnswerCount(key, buildingBlocks)
    return c.total > 0 && c.answered === c.total
  })

// NOTE: the headline "Profile strength" % is owned by the backend
// (RecoverProfile.total_optimization_score, surfaced as
// state.disputesConfigure.profile.totalOptimizationScore). We deliberately do NOT compute
// it on the client too, so the ring can't disagree with the stored value on
// refresh. The per-section counts above are a live answered-N-of-M affordance,
// not the score.
